/**
 * TopIapWidget - Топ инапов по выручке + разрезы покупок
 * =======================================================
 *
 * Три вьюхи, переключаются тумблером в шапке виджета:
 *
 * 1) «Общий»       - таблица топ-SKU за период: выручка, доля выручки, покупки,
 *                    покупатели, средний чек, выручка на покупателя.
 * 2) «По уровням»  - на каких уровнях кампании (acc_progress) покупают выбранный
 *                    SKU и ИЗ КАКОГО ЧИСЛА ЛЮДЕЙ: столбцы - покупатели, линия -
 *                    конверсия покупателей от всех активных игроков на этих уровнях.
 * 3) «По лайфтайму»- то же самое по оси дня жизни игрока (lifetime_days).
 *
 * Данные из ClickHouse (gamegears.prod_events_hole_alien), только валидные
 * покупки (event_name='iap', p2int=1). Диапазон дат дашборда - по дате СОБЫТИЯ,
 * а не по когорте регистрации.
 */

import React, { useState, useMemo } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { MetricsService } from '../../services/metricsService';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { iap as iapSpecs } from '@ggstat/chart-specs';

/** Сколько SKU запрашиваем во всех вьюхах. */
const TOP_LIMIT = 20;

/** Значение селектора «все инапы вместе» (не может совпасть с индексом SKU). */
const ALL_ITEMS = 'all';

/** Размеры корзины по оси. Должны совпадать с белым списком на сервере. */
const BUCKET_SIZES = {
  progress: [1, 5, 10, 25, 50],
  lifetime: [1, 7, 14, 30]
};

const DEFAULT_BUCKET = { progress: 10, lifetime: 7 };

const AXIS_LABEL = iapSpecs.breakdown.axisLabel;

/** Ряды разреза (столбики покупателей + линия конверсии) — общие с MCP. */
const [BUYERS_SERIES, CONVERSION_SERIES] = iapSpecs.breakdown.series;

const fmtInt = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });

const fmtMoney = (v) =>
  `$${Number(v || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtPct = (v, digits = 2) => `${Number(v || 0).toFixed(digits)}%`;

/** 'bosshunt_event_base_siege_pack_5_$4.99' -> цена из хвоста SKU, если она там есть. */
function priceFromSku(itemId) {
  const m = /\$([0-9]+(?:\.[0-9]+)?)$/.exec(String(itemId));
  return m ? Number(m[1]) : null;
}

/** Заголовок таблицы с сортировкой по клику. */
function SortableHead({ label, sortKey, sortConfig, onSort, className = '' }) {
  return (
    <TableHead
      className={`cursor-pointer hover:bg-muted/50 select-none ${className}`}
      onClick={() => onSort(sortKey)}
    >
      {label} {sortConfig.key === sortKey && (sortConfig.direction === 'asc' ? '↑' : '↓')}
    </TableHead>
  );
}

/** Вьюха «Общий»: таблица топ-SKU за период. */
function TotalView({ items, totals }) {
  const [sortConfig, setSortConfig] = useState({ key: 'revenue', direction: 'desc' });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sorted = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const av = a[sortConfig.key];
      const bv = b[sortConfig.key];
      const diff =
        typeof av === 'string' ? String(av).localeCompare(String(bv)) : Number(av) - Number(bv);
      return sortConfig.direction === 'asc' ? diff : -diff;
    });
    return copy;
  }, [items, sortConfig]);

  const totalRevenue = totals?.revenue || 0;

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">#</TableHead>
            <SortableHead label="SKU" sortKey="itemId" sortConfig={sortConfig} onSort={handleSort} />
            <SortableHead
              label="Выручка"
              sortKey="revenue"
              sortConfig={sortConfig}
              onSort={handleSort}
              className="text-right"
            />
            <TableHead className="text-right">Доля</TableHead>
            <SortableHead
              label="Покупки"
              sortKey="purchases"
              sortConfig={sortConfig}
              onSort={handleSort}
              className="text-right"
            />
            <SortableHead
              label="Покупатели"
              sortKey="buyers"
              sortConfig={sortConfig}
              onSort={handleSort}
              className="text-right"
            />
            <SortableHead
              label="Ср. чек"
              sortKey="avgCheck"
              sortConfig={sortConfig}
              onSort={handleSort}
              className="text-right"
            />
            <SortableHead
              label="На покупателя"
              sortKey="revenuePerBuyer"
              sortConfig={sortConfig}
              onSort={handleSort}
              className="text-right"
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((item, i) => (
            <TableRow key={item.itemId}>
              <TableCell className="text-muted-foreground">{i + 1}</TableCell>
              <TableCell className="font-mono text-xs">{item.itemId}</TableCell>
              <TableCell className="text-right font-medium">{fmtMoney(item.revenue)}</TableCell>
              <TableCell className="text-right text-muted-foreground">
                {totalRevenue > 0 ? fmtPct((item.revenue / totalRevenue) * 100, 1) : '—'}
              </TableCell>
              <TableCell className="text-right">{fmtInt(item.purchases)}</TableCell>
              <TableCell className="text-right">{fmtInt(item.buyers)}</TableCell>
              <TableCell className="text-right">{fmtMoney(item.avgCheck)}</TableCell>
              <TableCell className="text-right">{fmtMoney(item.revenuePerBuyer)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={2}>Всего по всем инапам за период</TableCell>
            <TableCell className="text-right font-medium">{fmtMoney(totalRevenue)}</TableCell>
            <TableCell className="text-right text-muted-foreground">100%</TableCell>
            <TableCell className="text-right">{fmtInt(totals?.purchases)}</TableCell>
            <TableCell className="text-right">{fmtInt(totals?.payers)}</TableCell>
            <TableCell colSpan={2} className="text-right text-muted-foreground text-xs">
              покупатели — уникальные плательщики
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}

const BreakdownTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium mb-2">{label}</p>
      <p>Активных игроков: {fmtInt(point.activeUsers)}</p>
      <p>Покупателей: {fmtInt(point.buyers)}</p>
      <p>Конверсия: {fmtPct(point.conversion, 3)}</p>
      <p>Покупок: {fmtInt(point.purchases)}</p>
      <p>Выручка: {fmtMoney(point.revenue)}</p>
    </div>
  );
};

/** Вьюха разреза: график конверсии по оси + таблица корзин. */
function BreakdownView({ data, dimension, selected, onSelect, bucket, onBucketChange }) {
  const { items = [], buckets = [], cells = [], bucketTotals = [], bucketSize = 1 } = data || {};

  // Точки графика: на каждую корзину оси — знаменатель (активные игроки) и
  // числитель по выбранному SKU (или по всем инапам сразу).
  const points = useMemo(() => {
    const byBucket = new Map();
    const source = selected === ALL_ITEMS ? bucketTotals : cells.filter((c) => c.item === Number(selected));
    source.forEach((row) => byBucket.set(row.bucket, row));

    return buckets.map((b, i) => {
      const row = byBucket.get(i);
      const buyers = row?.buyers || 0;
      return {
        label: bucketSize === 1 ? String(b.start) : `${b.start}–${b.end}`,
        activeUsers: b.activeUsers,
        buyers,
        purchases: row?.purchases || 0,
        revenue: row?.revenue || 0,
        conversion: b.activeUsers > 0 ? (buyers / b.activeUsers) * 100 : 0,
        revenuePerActive: b.activeUsers > 0 ? (row?.revenue || 0) / b.activeUsers : 0
      };
    });
  }, [buckets, cells, bucketTotals, selected, bucketSize]);

  const totals = useMemo(
    () =>
      points.reduce(
        (acc, p) => ({
          buyers: acc.buyers + p.buyers,
          purchases: acc.purchases + p.purchases,
          revenue: acc.revenue + p.revenue
        }),
        { buyers: 0, purchases: 0, revenue: 0 }
      ),
    [points]
  );

  const selectedLabel =
    selected === ALL_ITEMS ? 'Все инапы' : items[Number(selected)]?.itemId || '—';
  const price = selected === ALL_ITEMS ? null : priceFromSku(selectedLabel);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={String(selected)} onValueChange={onSelect}>
          <SelectTrigger className="w-full sm:w-[420px] h-9 text-xs">
            <SelectValue placeholder="Выберите инап" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_ITEMS}>Все инапы (любая покупка)</SelectItem>
            {items.map((item, i) => (
              <SelectItem key={item.itemId} value={String(i)}>
                {item.itemId} — {fmtMoney(item.revenue)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Шаг оси</span>
          <ToggleGroup
            type="single"
            value={String(bucket)}
            onValueChange={(value) => value && onBucketChange(Number(value))}
          >
            {BUCKET_SIZES[dimension].map((size) => (
              <ToggleGroupItem key={size} value={String(size)} aria-label={`Шаг ${size}`}>
                {size}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      {points.length === 0 ? (
        <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
          Нет данных за выбранный период
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={420}>
            <ComposedChart data={points} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="label"
                tick={{ fill: 'currentColor' }}
                className="text-xs"
                label={{ value: AXIS_LABEL[dimension], position: 'insideBottom', offset: -2 }}
              />
              <YAxis
                yAxisId="left"
                tick={{ fill: 'currentColor' }}
                className="text-xs"
                allowDecimals={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fill: 'currentColor' }}
                className="text-xs"
                tickFormatter={(v) => `${Number(v).toFixed(1)}%`}
              />
              <Tooltip content={<BreakdownTooltip />} />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey={BUYERS_SERIES.key}
                name={BUYERS_SERIES.label}
                fill={BUYERS_SERIES.color}
                isAnimationActive={false}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey={CONVERSION_SERIES.key}
                name={CONVERSION_SERIES.label}
                stroke={CONVERSION_SERIES.color}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>

          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{AXIS_LABEL[dimension]}</TableHead>
                  <TableHead className="text-right">Активные игроки</TableHead>
                  <TableHead className="text-right">Покупатели</TableHead>
                  <TableHead className="text-right">Конверсия</TableHead>
                  <TableHead className="text-right">Покупки</TableHead>
                  <TableHead className="text-right">Выручка</TableHead>
                  <TableHead className="text-right">Выручка на игрока</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {points.map((p) => (
                  <TableRow key={p.label}>
                    <TableCell>{p.label}</TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {fmtInt(p.activeUsers)}
                    </TableCell>
                    <TableCell className="text-right">{fmtInt(p.buyers)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {fmtPct(p.conversion, 3)}
                    </TableCell>
                    <TableCell className="text-right">{fmtInt(p.purchases)}</TableCell>
                    <TableCell className="text-right">{fmtMoney(p.revenue)}</TableCell>
                    <TableCell className="text-right">{fmtMoney(p.revenuePerActive)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell>
                    Итого{price ? ` · цена SKU $${price}` : ''}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground text-xs">
                    сумма по корзинам ≠ уникальные игроки
                  </TableCell>
                  <TableCell className="text-right">{fmtInt(totals.buyers)}</TableCell>
                  <TableCell />
                  <TableCell className="text-right">{fmtInt(totals.purchases)}</TableCell>
                  <TableCell className="text-right">{fmtMoney(totals.revenue)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

export function TopIapWidget() {
  const [view, setView] = useState('total');
  const [bucket, setBucket] = useState(DEFAULT_BUCKET.progress);
  const [selected, setSelected] = useState(ALL_ITEMS);

  const widgetData = useWidgetData(
    async (filters, options) =>
      view === 'total'
        ? MetricsService.getTopIapItems(filters, TOP_LIMIT, options)
        : MetricsService.getIapBreakdown(filters, view, bucket, TOP_LIMIT, options),
    [view, bucket]
  );

  const widgetProps = useBaseWidget(widgetData);
  const { data } = widgetProps;

  // Переключение вьюхи: у осей разные шаги корзины, поэтому шаг сбрасываем
  // на дефолтный для новой оси. Выбранный SKU сохраняем — индексы топа
  // одинаковые в обоих разрезах (один и тот же порядок по выручке).
  const handleViewChange = (next) => {
    if (!next) return;
    setView(next);
    if (next !== 'total') {
      setBucket(DEFAULT_BUCKET[next]);
    }
  };

  const Controls = () => (
    <ToggleGroup type="single" value={view} onValueChange={handleViewChange}>
      <ToggleGroupItem value="total" aria-label="Общий">Общий</ToggleGroupItem>
      <ToggleGroupItem value="progress" aria-label="По уровням">По уровням</ToggleGroupItem>
      <ToggleGroupItem value="lifetime" aria-label="По лайфтайму">По лайфтайму</ToggleGroupItem>
    </ToggleGroup>
  );

  const dateRange = data?.dateRange;
  const subtitle = dateRange?.start
    ? `Период покупок: ${dateRange.start} — ${dateRange.end} · только валидные покупки`
    : null;

  const title =
    view === 'total'
      ? 'Топ инапов по выручке'
      : view === 'progress'
        ? 'Покупки инапов по уровню кампании'
        : 'Покупки инапов по дню жизни игрока';

  return (
    <BaseWidget
      {...widgetProps}
      title={title}
      subtitle={subtitle}
      headerAction={<Controls />}
      emptyMessage="Нет покупок за выбранный период"
      skeletonRows={10}
    >
      {view === 'total' ? (
        <TotalView items={data?.items || []} totals={data?.totals} />
      ) : (
        <BreakdownView
          data={data}
          dimension={view}
          selected={selected}
          onSelect={setSelected}
          bucket={bucket}
          onBucketChange={setBucket}
        />
      )}
    </BaseWidget>
  );
}

export default TopIapWidget;
